import React, { useState, useEffect } from 'react';
import { FaPhone, FaPlus, FaTrash, FaSave, FaSearch, FaUserShield } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as adminService from '../services/adminService';
import * as authService from '../services/authService';

const AuthorizedNumbers = () => {
    const [authorizedNumbers, setAuthorizedNumbers] = useState([]);
    const [roles, setRoles] = useState([]); // Store fetched roles
    const [formData, setFormData] = useState({
        phone_number: '',
        role: '' // Will be set to editorial role ID once loaded
    });
    const [toast, setToast] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Fetch roles and authorized numbers on mount
    useEffect(() => {
        const loadInitialData = async () => {
            // 1. Fetch Roles
            try {
                const rolesData = await authService.fetchRoles();
                // Filter for just Editorial and Reviewer
                const relevantRoles = rolesData.filter(r =>
                    r.code === 'editorial' || r.code === 'reviewer'
                );

                // Map to simpler format if needed, or just use as is. 
                // We need to know which one is editorial to set default
                const formattedRoles = relevantRoles.map(r => ({
                    id: r.id,
                    label: r.code === 'editorial' ? 'Editorial Board' : 'Reviewer',
                    code: r.code
                }));

                setRoles(formattedRoles);

                // No longer defaulting to Editorial Board, allowing user to select
                // setFormData(prev => ({ ...prev, role: '' }));

            } catch (error) {
                console.error('Error fetching roles:', error);
                showToast('Failed to load roles', 'error');
            }

            // 2. Fetch Authorized Numbers
            await fetchAuthorizedNumbers();
        };

        loadInitialData();
    }, []);

    const fetchAuthorizedNumbers = async () => {
        try {
            setIsLoading(true);
            const response = await adminService.getAuthorizedNumbers();
            if (response && response.success && Array.isArray(response.data)) {
                setAuthorizedNumbers(response.data);
            } else if (Array.isArray(response)) {
                setAuthorizedNumbers(response);
            } else {
                setAuthorizedNumbers([]);
            }
        } catch (error) {
            console.error('Error fetching authorized numbers:', error);
            showToast(error.message || 'Failed to fetch authorized numbers', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        // Find the role ID that matches the item's role
        const roleId = getRoleIdentifier(item);

        // Try to match strict ID first, then code
        let matchedRole = roles.find(r => r.id.toLowerCase() === roleId.toLowerCase());
        if (!matchedRole) {
            // If not found by ID, try code
            if (roleId === 'editorial' || roleId === 'editorial_board') {
                matchedRole = roles.find(r => r.code === 'editorial');
            } else if (roleId === 'reviewer') {
                matchedRole = roles.find(r => r.code === 'reviewer');
            }
        }

        // If still no match, fallback to the item's role_id directly if it looks like a UUID
        // This ensures we preserve the ID even if we can't find it in our current filtered list
        const fallbackRole = matchedRole ? matchedRole.id : (item.role_id || '');

        setFormData({
            phone_number: item.phone_number,
            role: fallbackRole
        });

        // Scroll to form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.phone_number.trim()) {
            showToast('Please enter a phone number', 'error');
            return;
        }

        if (!formData.role) {
            showToast('Please select a role', 'error');
            return;
        }

        // Basic phone number validation
        if (formData.phone_number.length !== 10) {
            showToast('Please enter a valid 10-digit phone number', 'error');
            return;
        }

        try {
            setIsLoading(true);
            const payload = {
                phone_number: formData.phone_number,
                role_id: formData.role
            };

            if (editingId) {
                // Include description for updates in case legacy validation requires it
                const selectedRole = roles.find(r => r.id === formData.role);
                if (selectedRole) {
                    payload.description = selectedRole.label;
                }

                await adminService.updateAuthorizedNumber(editingId, payload);
                showToast('Authorized number updated successfully', 'success');
                setEditingId(null);
            } else {
                await adminService.createAuthorizedNumber(payload);
                showToast('Authorized number added successfully', 'success');
            }

            // Reset form
            setFormData({
                phone_number: '',
                role: ''
            });

            // Refresh list
            await fetchAuthorizedNumbers();

        } catch (error) {
            console.error('Error saving authorized number:', error);
            showToast(error.message || 'Failed to save authorized number', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this authorized number?')) {
            try {
                setIsLoading(true);
                await adminService.deleteAuthorizedNumber(id);
                showToast('Authorized number deleted successfully', 'success');
                await fetchAuthorizedNumbers();
            } catch (error) {
                console.error('Error deleting authorized number:', error);
                showToast(error.message || 'Failed to delete authorized number', 'error');
            } finally {
                setIsLoading(false);
            }
        }
    };
    const getRoleIdentifier = (item) => {
        // Prioritize nested role object with code
        if (item.role && typeof item.role === 'object' && item.role.code) {
            return item.role.code.toLowerCase();
        }

        // Also check if item.role is an object with just an ID 
        if (item.role && typeof item.role === 'object' && item.role.id) {
            return item.role.id.toLowerCase();
        }

        // Check if role is just a string code
        if (typeof item.role === 'string') {
            return item.role.toLowerCase();
        }

        // Check other common fields
        if (item.role_code) return item.role_code.toLowerCase();
        if (item.type) return item.type.toLowerCase();

        // Lastly check role_id
        if (item.role_id) return item.role_id.toString().toLowerCase();

        return '';
    };

    const isEditorial = (item) => {
        const id = getRoleIdentifier(item);
        // Check hardcoded legacy IDs AND new dynamic logic
        // Also check against the loaded roles to be safe
        const editorialRole = roles.find(r => r.code === 'editorial');
        return id === 'editorial' || id === 'editorial_board' || id === 'board' || id === '6' || (editorialRole && id === editorialRole.id.toLowerCase());
    };

    const isReviewer = (item) => {
        const id = getRoleIdentifier(item);
        const reviewerRole = roles.find(r => r.code === 'reviewer');
        return id === 'reviewer' || id === '5' || (reviewerRole && id === reviewerRole.id.toLowerCase());
    };

    const getRoleLabel = (item) => {
        if (isEditorial(item)) return 'Editorial Board';
        if (isReviewer(item)) return 'Reviewer';

        // Try to match with loaded roles as fallback
        const matchedRole = roles.find(r => r.id.toLowerCase() === getRoleIdentifier(item));
        if (matchedRole) return matchedRole.label;

        return 'Unknown Role';
    };

    const filteredNumbers = authorizedNumbers.filter(item => {
        const identifier = getRoleIdentifier(item);
        const term = searchTerm.toLowerCase();
        return item.phone_number.includes(term) || identifier.includes(term);
    });

    const darkGradient = {
        background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <div className="max-w mx-auto">
                {/* Header */}
                <div className="rounded-2xl shadow-xl p-6 mb-8 transform hover:scale-[1.01] transition-all duration-300" style={darkGradient}>
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center shadow-lg">
                            <FaPhone className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-white">Authorized Numbers</h1>
                            <p className="text-blue-100 mt-1">Manage numbers authorized for Editorial and Reviewer roles</p>
                        </div>
                    </div>
                </div>

                {/* Form Section */}
                <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-indigo-50">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <FaPlus className="text-indigo-600" />
                        {editingId ? 'Update Authorized Number' : 'Authorize New Number'}
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Phone Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <FaPhone className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="phone_number"
                                        value={formData.phone_number}
                                        onChange={handleInputChange}
                                        placeholder="Enter 10-digit number"
                                        maxLength="10"
                                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Assign Role <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <FaUserShield className="text-gray-400" />
                                    </div>
                                    <select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                        className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none bg-white appearance-none"
                                        required
                                        disabled={isLoading}
                                    >
                                        <option value="">Please Select Role</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>
                                                {role.label}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-gray-400">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end pt-4">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform active:scale-95 disabled:opacity-50"
                            >
                                {isLoading ? (
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                ) : (
                                    <FaSave />
                                )}
                                {editingId ? 'Update Number' : 'Authorize Number'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* List Section */}
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-indigo-50">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
                        <h2 className="text-2xl font-bold text-gray-900">
                            Authorized Registry ({authorizedNumbers.length})
                        </h2>

                        <div className="relative">
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by number or role..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all w-full md:w-80 outline-none"
                            />
                        </div>
                    </div>

                    {isLoading && authorizedNumbers.length === 0 ? (
                        <div className="text-center py-20 grayscale opacity-50">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
                            <p className="text-gray-500 text-lg font-medium">Synchronizing registry...</p>
                        </div>
                    ) : filteredNumbers.length === 0 ? (
                        <div className="text-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaPhone className="text-3xl text-gray-300" />
                            </div>
                            <p className="text-gray-500 text-xl font-semibold">
                                {searchTerm ? 'No matches found in the registry' : 'No numbers have been authorized yet'}
                            </p>
                            <p className="text-gray-400 mt-2">Add a number above to get started</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-2xl border border-gray-100">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50/50">
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">S.No</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Phone Number</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role Assigned</th>
                                        <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredNumbers.map((item, index) => (
                                        <tr key={item.id} className="hover:bg-indigo-50/30 transition-colors group">
                                            <td className="px-6 py-4 text-sm text-gray-500 font-medium">{index + 1}</td>
                                            <td className="px-6 py-4 text-sm text-gray-900 font-bold font-mono tracking-wider">
                                                {item.phone_number}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold tracking-wide ${isEditorial(item)
                                                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                                                    : 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                                                    }`}>
                                                    {getRoleLabel(item)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                                                    <span className="text-xs font-semibold text-gray-600">Active Authority</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        className="p-2.5 bg-indigo-50 text-indigo-500 rounded-xl hover:bg-indigo-500 hover:text-white transition-all duration-300 shadow-sm group-hover:shadow-md"
                                                        title="Edit"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.id)}
                                                        className="p-2.5 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all duration-300 shadow-sm group-hover:shadow-md"
                                                        title="Revoke Authority"
                                                    >
                                                        <FaTrash className="text-sm" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuthorizedNumbers;